/**
 * Document role authority of a frozen scenario's declared available-consumption
 * input (`availableWaterConsumptionGalPerDay`).
 *
 * W7 (report projection) and W6 (water guard) used to decide that input's
 * role only by numeric equality with the SED whole-site ledger. A consumptive
 * water entitlement accepted by the extraction validator (W2) and the
 * screening prefill (W3) can coincide with the site allocation, and was then
 * called "not a consumptive-use limit". Lane A's role binding is the authority
 * when the frozen input has document provenance:
 *
 *   - 'documented-consumptive-limit': every frozen document excerpt prints this
 *     exact value in some unit (gal/day, MGD or AFY) and the shared classifier
 *     binds each printing in that unit to a consumptive-use limit
 *     (screeningWaterFactRoleIsBound), as W3 binds a fact per raw unit. It is
 *     a documented limit whatever the ledger says.
 *   - 'document-unbound': document provenance exists but does not bind this
 *     value to that role (no excerpt, no unit-bound printing of the value, or
 *     every unit group holds a printing with another or UNKNOWN role). It is
 *     not a documented limit, even when no ledger value matches.
 *   - 'no-document': no document provenance; the ledger comparison decides.
 *
 * Pure. Reads only the frozen assumptions; never the live documents.
 */
import type { GenerationAssumption } from '../../types/generationImpact';
import { DEFAULT_WHOLE_SITE_WATER_TOLERANCE_GAL_PER_DAY } from '../water/waterQuantityRoles';
import { convertConsumptiveUseAfyToGalPerDay, convertMgdToGalPerDay } from './screeningEvidencePrefill';
import {
  numericSourceTokens,
  screeningWaterFactRoleIsBound,
  sourceQuantityHasUnit,
  type SourceNumericToken,
} from './screeningWaterFactRoles';

export type DeclaredWaterConsumptionProvenance =
  | 'documented-consumptive-limit'
  | 'document-unbound'
  | 'no-document';

const FIELD = 'availableWaterConsumptionGalPerDay' as const;

/**
 * The printed units the extraction validator canonicalizes for this field,
 * each with that same canonical conversion to gal/day. The frozen value is the
 * canonical gal/day figure, so a printing is this value when its own unit-bound
 * conversion equals it.
 */
const DOCUMENT_WATER_UNITS: ReadonlyArray<readonly [unit: string, toGalPerDay: (value: number) => number | null]> = [
  ['gal/day', value => value],
  ['MGD', convertMgdToGalPerDay],
  ['AFY', convertConsumptiveUseAfyToGalPerDay],
];

/**
 * Printings of `galPerDay` in `excerpt` in one water unit only, each bound to
 * that unit. W3 (screeningWaterRoleTokens) selects a fact's printings in its
 * own raw unit only, so the same quantity printed in another unit is another
 * statement and is read as its own unit group.
 */
function printedValueTokens(
  excerpt: string, galPerDay: number, unit: string, toGalPerDay: (value: number) => number | null,
): SourceNumericToken[] {
  const tokens = numericSourceTokens(excerpt);
  return tokens.filter(token => {
    const converted = toGalPerDay(token.value);
    return converted != null
      && Math.abs(converted - galPerDay) <= DEFAULT_WHOLE_SITE_WATER_TOLERANCE_GAL_PER_DAY
      && sourceQuantityHasUnit(excerpt, token, tokens, unit, undefined, FIELD);
  });
}

/**
 * One excerpt binds the value when some unit group has printings of it and
 * the shared classifier binds every printing in that group to a
 * consumptive-use limit, exactly as W3 binds a fact per raw unit. A group with
 * printings of mixed or UNKNOWN role fails closed.
 */
function excerptBindsValue(excerpt: string, galPerDay: number): boolean {
  return DOCUMENT_WATER_UNITS.some(([unit, toGalPerDay]) =>
    screeningWaterFactRoleIsBound(FIELD, excerpt, printedValueTokens(excerpt, galPerDay, unit, toGalPerDay)));
}

/**
 * The role authority for the declared available-consumption value
 * `galPerDay` in a frozen scenario input. The governing assumption is the last
 * one recorded for the field, as the screening prefill reads it.
 */
export function declaredWaterConsumptionProvenance(
  input: { readonly assumptions?: readonly GenerationAssumption[] } | null | undefined,
  galPerDay: number | null | undefined,
): DeclaredWaterConsumptionProvenance {
  const assumption = [...(input?.assumptions ?? [])].reverse().find(row => row.field === FIELD);
  if (!assumption) return 'no-document';
  const documentRefs = (assumption.evidenceRefs ?? []).filter(ref => ref.kind === 'document');
  if (assumption.source !== 'document' && !documentRefs.length) return 'no-document';
  if (galPerDay == null || !Number.isFinite(galPerDay) || galPerDay < 0) return 'document-unbound';
  const excerpts = documentRefs.map(ref => (ref.kind === 'document' ? ref.excerpt ?? '' : ''));
  const bound = excerpts.length > 0 && excerpts.every(excerpt => excerpt.trim().length > 0
    && excerptBindsValue(excerpt, galPerDay));
  return bound ? 'documented-consumptive-limit' : 'document-unbound';
}
